import { useTranslations } from 'next-intl';
import { useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { getTaskCredits, getTaskCreditsV2 } from '@/app/apis';
import { toast } from '@/app/components/ui/use-toast';
import { SCENE } from '@/app/constants';
import type { TaskParams } from '@/app/types';
import { useFetch } from './use-fetch';

const DEFAULT_TEXT_CREDITS_BUCKET_SIZE = 100;
type TaskCreditsQuery = NonNullable<NonNullable<Parameters<typeof getTaskCredits>[0]>['query']>;
type TaskCreditsSettingsQuery = Record<`settings.${string}`, unknown>;

interface CostCreditsParams {
  submitValue: TaskParams;
  /** V2 工具使用独立后端计价 */
  useV2ToolTaskApi?: boolean;
  templateScene?: string;
  /** 是否在0积分试用内 */
  inZeroCreditsTrials: boolean;
  /** 当检测到 ImagePromoCreationShortage 错误时的回调，用于限制数量为1 */
  onImagePromoCreationShortage?: () => void;
  /** 文本积分分桶大小，每跨过一个桶才重新请求积分 */
  textCreditsBucketSize?: number;
  /** 外部传入用于文本积分分桶的长度，未传时退回 submitValue.text.length */
  textCreditsMeasuredLength?: number;
  /** 仅用于积分估算请求的文本，不会进入实际任务提交参数 */
  creditsText?: string;
}

/**
 *
 * @param submitValue 提交值
 * @param templateScene 模板场景
 * @param inZeroCreditsTrials 是否在0积分试用内
 * @param onImagePromoCreationShortage 当检测到 ImagePromoCreationShortage 错误时的回调
 * @param textCreditsBucketSize 文本积分分桶大小，每跨过一个桶才重新请求积分
 * @param textCreditsMeasuredLength 外部传入用于文本积分分桶的长度
 * @returns costCredits 所需积分，originalCredits 原价积分
 */
export const useCostCredits = ({
  submitValue,
  useV2ToolTaskApi = false,
  templateScene,
  inZeroCreditsTrials,
  onImagePromoCreationShortage,
  textCreditsBucketSize = DEFAULT_TEXT_CREDITS_BUCKET_SIZE,
  textCreditsMeasuredLength,
  creditsText,
}: CostCreditsParams) => {
  const t = useTranslations();
  const normalizedTextCreditsBucketSize = Math.max(1, textCreditsBucketSize);

  // 稳定 flattenedSettings 对象的引用，只有当实际内容变化时才更新（浅比较）
  const prevRef = useRef<TaskCreditsSettingsQuery | undefined>(undefined);
  const stableFlattenedSettings = useMemo(() => {
    const flattenedSettings: TaskCreditsSettingsQuery = {};

    const appendSettings = (value: unknown, path: string) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([childKey, childValue]) => {
          appendSettings(childValue, `${path}.${childKey}`);
        });
        return;
      }

      flattenedSettings[`settings.${path}`] = value;
    };

    Object.entries(submitValue.settings).forEach(([key, value]) => {
      appendSettings(value, key);
    });

    // 浅比较：如果内容相同，返回上一次的对象引用
    if (prevRef.current && shallow(prevRef.current, flattenedSettings)) {
      return prevRef.current;
    }

    prevRef.current = flattenedSettings;
    return flattenedSettings;
  }, [submitValue.settings]);

  const textCreditsBucketIndex = useMemo(() => {
    const resolvedTextLength = textCreditsMeasuredLength ?? creditsText?.length ?? submitValue.text?.length ?? 0;

    if (resolvedTextLength === 0) {
      return 0;
    }

    return Math.ceil(resolvedTextLength / normalizedTextCreditsBucketSize);
  }, [creditsText, normalizedTextCreditsBucketSize, submitValue.text, textCreditsMeasuredLength]);

  const resolvedCreditsText = creditsText ?? submitValue.text;

  const taskCreditsQuery = useMemo<TaskCreditsQuery>(() => {
    const query: TaskCreditsQuery = {
      type: submitValue.type,
      ...(stableFlattenedSettings as Partial<TaskCreditsQuery>),
      [SCENE]: templateScene,
    };

    if (resolvedCreditsText !== undefined) {
      query.text = resolvedCreditsText;
    }

    return query;
  }, [creditsText, stableFlattenedSettings, submitValue.text, submitValue.type, templateScene]);

  // 接口：获取任务所需积分
  const { data: creditsRes } = useFetch(
    () =>
      (useV2ToolTaskApi ? getTaskCreditsV2 : getTaskCredits)({
        query: taskCreditsQuery,
      }),
    {
      refreshDeps: [
        useV2ToolTaskApi,
        submitValue.type,
        stableFlattenedSettings,
        templateScene,
        textCreditsBucketIndex,
        resolvedCreditsText,
      ],
      debounceWait: 500,
      onSuccess: (data) => {
        if (data?.error) {
          const error = data.error;
          if (error.reason === 'ImagePromoCreationShortage') {
            toast({
              type: 'error',
              message: t('popup.toast.imagePromoCreationShortage'),
            });
            onImagePromoCreationShortage?.();
          }
        }
      },
    },
  );

  const [costCredits, originalCredits] = useMemo(() => {
    if (inZeroCreditsTrials) {
      return [0, undefined];
    }
    const costCredits = creditsRes?.data?.cost_credits;
    const originalCredits = creditsRes?.data?.original_cost_credits;

    // 只有当原价大于现价时，才显示原价
    if (originalCredits && costCredits && originalCredits > costCredits) {
      return [costCredits, originalCredits];
    }

    return [costCredits, undefined];
  }, [creditsRes, inZeroCreditsTrials]);

  return {
    costCredits,
    originalCredits,
  };
};
